import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import config from '../config/config';
import './VigilScanHistory.css';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';

/* ── severity palette ──────────────────────────────────────────────────────── */
const SEV_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#ca8a04',
  low: '#2563eb',
};

/* ── helpers ───────────────────────────────────────────────────────────────── */
function relativeTime(dateStr) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function formatDuration(dur) {
  if (!dur) return '—';
  if (typeof dur === 'string') return dur;
  if (dur < 1000) return `${dur}ms`;
  return `${(dur / 1000).toFixed(1)}s`;
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleString();
}

/* ── motion variants ───────────────────────────────────────────────────────── */
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.4, ease: 'easeOut' },
  }),
};

/* ── chart tooltip ─────────────────────────────────────────────────────────── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  return (
    <div style={{
      background: 'var(--bs-body-bg, #fff)', border: '1px solid var(--bs-border-color)',
      borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.82rem',
      boxShadow: '0 4px 12px rgba(0,0,0,.1)',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
          <span style={{ textTransform: 'capitalize' }}>{p.dataKey}:</span>
          <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function VigilScanHistory() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // sorting
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  /* ── fetch scans ─────────────────────────────────────────────────────────── */
  const fetchScans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/scans`, {
        params: { limit: 100 },
      });
      setScans(data.scans || []);
    } catch (err) {
      console.error('Failed to fetch scans:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchScans(); }, [fetchScans]);

  /* ── derived data ────────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    let result = scans;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(s => (s.repoName || s.repoPath || '').toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      result = result.filter(s => s.status === statusFilter);
    }
    // sort
    result = [...result].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'createdAt' || sortKey === 'updatedAt') {
        av = new Date(av || 0).getTime();
        bv = new Date(bv || 0).getTime();
      }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [scans, search, statusFilter, sortKey, sortDir]);

  const stats = useMemo(() => {
    const totalScans = scans.length;
    const totalFindings = scans.reduce((s, x) => s + (x.totalFindings || 0), 0);
    const avgFindings = totalScans ? Math.round(totalFindings / totalScans) : 0;
    const reposScanned = new Set(scans.map(s => s.repoName || s.repoPath).filter(Boolean)).size;
    return { totalScans, totalFindings, avgFindings, reposScanned };
  }, [scans]);

  const trendData = useMemo(() => {
    const sorted = [...scans]
      .filter(s => s.createdAt)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return sorted.map(s => ({
      date: new Date(s.createdAt).toLocaleDateString(),
      critical: s.critical || 0,
      high: s.high || 0,
      medium: s.medium || 0,
      low: s.low || 0,
    }));
  }, [scans]);

  const pagedScans = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return filtered.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filtered, page]);

  /* ── sort handler ────────────────────────────────────────────────────────── */
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? '▲' : '▼';
  };

  /* ── re-run handler ──────────────────────────────────────────────────────── */
  const handleRerun = (scan) => {
    navigate('/vigil/scan', { state: { repoPath: scan.repoPath, rulePacks: scan.rulePacks } });
  };

  /* ── delete handler ──────────────────────────────────────────────────────── */
  const handleDelete = useCallback(async (scanId) => {
    if (!window.confirm('Delete this scan permanently? This cannot be undone.')) return;
    try {
      await axios.delete(`${config.BASE_URL}/vigil/scan/${scanId}`);
      setScans(prev => prev.filter(s => s.id !== scanId));
    } catch (err) {
      console.error('Failed to delete scan:', err);
      alert('Failed to delete scan: ' + (err.response?.data?.error || err.message));
    }
  }, []);

  /* ── auto-refresh while any scan is still running ────────────────────────── */
  useEffect(() => {
    const hasRunning = scans.some(s => s.status === 'scanning' || s.status === 'pending');
    if (!hasRunning) return;
    const timer = setInterval(fetchScans, 5000);
    return () => clearInterval(timer);
  }, [scans, fetchScans]);

  /* ── render ──────────────────────────────────────────────────────────────── */
  const statCards = [
    { key: 'totalScans', icon: '📊', value: stats.totalScans, label: t('vigilHistory.totalScans'), color: '#6366f1' },
    { key: 'totalFindings', icon: '🔍', value: stats.totalFindings, label: t('vigilHistory.totalFindings'), color: '#dc2626' },
    { key: 'avgFindings', icon: '📈', value: stats.avgFindings, label: t('vigilHistory.avgFindings'), color: '#ea580c' },
    { key: 'reposScanned', icon: '📁', value: stats.reposScanned, label: t('vigilHistory.reposScanned'), color: '#2563eb' },
  ];

  const statusChips = [
    { value: 'all', label: t('vigilHistory.allStatuses') },
    { value: 'done', label: 'Done' },
    { value: 'failed', label: 'Failed' },
  ];

  const columns = [
    { key: 'status', label: t('vigilHistory.status'), sortable: true },
    { key: 'repoName', label: t('vigilHistory.repository'), sortable: true },
    { key: 'totalFindings', label: t('vigilHistory.findings'), sortable: true },
    { key: 'critical', label: 'Critical', sortable: true },
    { key: 'high', label: 'High', sortable: true },
    { key: 'medium', label: 'Medium', sortable: true },
    { key: 'low', label: 'Low', sortable: true },
    { key: 'duration', label: t('vigilHistory.duration'), sortable: true },
    { key: 'createdAt', label: t('vigilHistory.date'), sortable: true },
    { key: 'actions', label: t('vigilHistory.actions'), sortable: false },
  ];

  /* ── mini severity bar ───────────────────────────────────────────────────── */
  const MiniBar = ({ scan }) => {
    const total = (scan.critical || 0) + (scan.high || 0) + (scan.medium || 0) + (scan.low || 0);
    if (!total) return <div className="vigil-history-minibar" />;
    const segs = [
      { key: 'critical', val: scan.critical || 0, color: SEV_COLORS.critical },
      { key: 'high', val: scan.high || 0, color: SEV_COLORS.high },
      { key: 'medium', val: scan.medium || 0, color: SEV_COLORS.medium },
      { key: 'low', val: scan.low || 0, color: SEV_COLORS.low },
    ];
    return (
      <div className="vigil-history-minibar">
        {segs.map(s => s.val > 0 && (
          <div key={s.key} className="vigil-history-minibar-seg"
            style={{ width: `${(s.val / total) * 100}%`, background: s.color }} />
        ))}
      </div>
    );
  };

  const statusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'done') return 'ce-status--resolved';
    if (s === 'failed') return 'ce-status--open';
    if (s === 'pending') return 'ce-status--in-progress';
    if (s === 'scanning') return 'ce-status--in-progress';
    return '';
  };

  return (
    <div className="vigil-history">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="ce-page-header mb-4 mt-3">
        <h2 className="ce-page-title">🕐 {t('vigilHistory.title')}</h2>
        <span style={{ fontSize: '0.9rem', color: 'var(--bs-secondary-color)' }}>
          {t('vigilHistory.subtitle')} — {stats.totalScans} {t('vigilHistory.totalScans').toLowerCase()}
        </span>
      </div>

      {/* ── Stats Row ──────────────────────────────────────────────────────── */}
      <div className="ce-kpi-row mb-4">
        {statCards.map((card, i) => (
          <motion.div
            key={card.key}
            className="ce-kpi"
            style={{ '--kpi-color': card.color }}
            custom={i}
            initial="hidden"
            animate="visible"
            variants={cardVariants}
          >
            <div className="ce-kpi-value">{card.value}</div>
            <div className="ce-kpi-label">{card.icon} {card.label}</div>
          </motion.div>
        ))}
      </div>

      {/* ── Trend Chart ────────────────────────────────────────────────────── */}
      {trendData.length > 1 && (
        <motion.div
          className="ce-card mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
        >
          <div className="ce-card-header">
            <span className="ce-card-title">{t('vigilHistory.trendTitle')}</span>
          </div>
          <div className="ce-card-body">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="critical" stackId="1" fill={SEV_COLORS.critical} stroke={SEV_COLORS.critical} fillOpacity={0.7} />
                <Area type="monotone" dataKey="high" stackId="1" fill={SEV_COLORS.high} stroke={SEV_COLORS.high} fillOpacity={0.6} />
                <Area type="monotone" dataKey="medium" stackId="1" fill={SEV_COLORS.medium} stroke={SEV_COLORS.medium} fillOpacity={0.5} />
                <Area type="monotone" dataKey="low" stackId="1" fill={SEV_COLORS.low} stroke={SEV_COLORS.low} fillOpacity={0.4} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* ── Filter Bar ─────────────────────────────────────────────────────── */}
      <div className="ce-filter-bar mb-3">
        <input
          type="text"
          className="ce-search-input"
          placeholder={t('vigilHistory.searchPlaceholder')}
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="ce-filter-select"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          {statusChips.map(chip => (
            <option key={chip.value} value={chip.value}>{chip.label}</option>
          ))}
        </select>
        <span className="ce-filter-count">{filtered.length} scans</span>
      </div>

      {/* ── Loading state ──────────────────────────────────────────────────── */}
      {loading && (
        <div className="vigil-history-loading">
          <span className="spinner-border spinner-border-sm" /> Loading scans…
        </div>
      )}

      {/* ── Error state ────────────────────────────────────────────────────── */}
      {error && !loading && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      {/* ── Empty state ────────────────────────────────────────────────────── */}
      {!loading && !error && scans.length === 0 && (
        <motion.div
          className="ce-blank"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <div className="ce-blank-icon">🛡️</div>
          <div className="ce-blank-title">{t('vigilHistory.noScans')}</div>
          <div className="ce-blank-sub">{t('vigilHistory.noScansDesc')}</div>
          <button
            className="ce-btn ce-btn--primary"
            onClick={() => navigate('/vigil/scan')}
          >
            {t('vigilHistory.runFirstScan')}
          </button>
        </motion.div>
      )}

      {/* ── Scans table ────────────────────────────────────────────────────── */}
      {!loading && !error && filtered.length > 0 && (
        <motion.div
          className="ce-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <div className="ce-card-body--flush">
            <div className="ce-table-wrap">
              <table className="ce-table">
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th
                        key={col.key}
                        className={col.sortable ? 'sortable' : ''}
                        onClick={col.sortable ? () => handleSort(col.key) : undefined}
                        style={{ cursor: col.sortable ? 'pointer' : undefined }}
                      >
                        {col.label}
                        {col.sortable && (
                          <span className="vigil-history-sort-icon" style={{ marginLeft: 4, opacity: 0.6 }}>{sortIcon(col.key)}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedScans.map(scan => (
                    <tr key={scan.id}>
                      <td>
                        <span className={`ce-status ${statusClass(scan.status)}`}>
                          {scan.status}
                        </span>
                      </td>
                      <td style={{ fontWeight: 500 }}>{scan.repoName || scan.repoPath || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600, minWidth: 24 }}>{scan.totalFindings || 0}</span>
                          <MiniBar scan={scan} />
                        </div>
                      </td>
                      <td><span className="ce-sev ce-sev--critical">{scan.critical || 0}</span></td>
                      <td><span className="ce-sev ce-sev--high">{scan.high || 0}</span></td>
                      <td><span className="ce-sev ce-sev--medium">{scan.medium || 0}</span></td>
                      <td><span className="ce-sev ce-sev--low">{scan.low || 0}</span></td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.84rem' }}>
                        {formatDuration(scan.duration)}
                      </td>
                      <td title={scan.createdAt ? formatDate(scan.createdAt) : ''}>
                        {scan.createdAt ? relativeTime(scan.createdAt) : '—'}
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-nowrap">
                          <button
                            className="ce-btn ce-btn--secondary ce-btn--sm"
                            onClick={() => navigate(`/vigil/scan?id=${scan.id}`)}
                          >
                            {t('vigilHistory.viewResults')}
                          </button>
                          <button
                            className="ce-btn ce-btn--secondary ce-btn--sm"
                            onClick={() => handleRerun(scan)}
                          >
                            {t('vigilHistory.rerun')}
                          </button>
                          <button
                            className="ce-btn ce-btn--danger ce-btn--sm"
                            title="Delete scan"
                            onClick={() => handleDelete(scan.id)}
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {filtered.length > DEFAULT_PAGE_SIZE && (
            <div className="ce-card-body" style={{ paddingTop: '0.75rem', paddingBottom: '0.75rem' }}>
              <Pagination
                nPages={Math.ceil(filtered.length / DEFAULT_PAGE_SIZE)}
                currentPage={page}
                setCurrentPage={setPage}
                totalItems={filtered.length}
                pageSize={DEFAULT_PAGE_SIZE}
              />
            </div>
          )}
        </motion.div>
      )}

      {/* filtered empty (scans exist but filter hides them) */}
      {!loading && !error && scans.length > 0 && filtered.length === 0 && (
        <div className="ce-blank">
          <div className="ce-blank-icon">🔎</div>
          <div className="ce-blank-title">No matching scans</div>
          <div className="ce-blank-sub">Try adjusting your search or filters</div>
        </div>
      )}
    </div>
  );
}
